/**
 * ClaudeCredentialModal — paste-token form for the user's Claude
 * credential, stored in workspace-credentials and read at deploy time
 * by the Managed Apps deploy gate (resolveAgentCredential in
 * backend/src/handlers/apps.js).
 *
 * Two input choices via a radio:
 *   - OAuth token (preferred — bills against the user's Claude Code
 *     subscription, minted via `claude setup-token`).
 *   - Anthropic API key (`sk-ant-api…` — bills against API credits).
 *
 * Uses the app's shared <MktAlertModal> (same `mkt-modal` shell as the app
 * deploy/preview modal + every other marketplace dialog) for the chrome.
 * The radio + input + Save live in its children. White PillButton, no Cancel.
 *
 * onConnected({ mode, suffix }) — caller refreshes "now using …abc123".
 */

import React, { useEffect, useState } from 'react';
import WorkspaceCredentials from '../../services/workspaceCredentials';
import MktAlertModal from '../molecules/MktAlertModal/MktAlertModal';
import PillButton from '../shared/PillButton';

const inputStyle = {
  width: '100%', padding: '10px 12px', borderRadius: 6,
  border: '1px solid rgba(255,255,255,0.15)', background: 'rgba(255,255,255,0.04)',
  color: '#fff', fontSize: 14, boxSizing: 'border-box',
};

const radioCard = (active, disabled) => ({
  flex: 1, cursor: disabled ? 'not-allowed' : 'pointer', padding: 10, borderRadius: 6,
  border: `1px solid ${active ? 'var(--ion-color-primary)' : 'rgba(255,255,255,0.15)'}`,
  background: active ? 'rgba(99,102,241,0.12)' : 'transparent',
});

export default function ClaudeCredentialModal({ isOpen, onClose, onConnected, defaultKind = 'oauth' }) {
  const [kind, setKind] = useState(defaultKind);
  const [token, setToken] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (isOpen) { setKind(defaultKind); setToken(''); setError(''); setSubmitting(false); }
  }, [isOpen, defaultKind]);

  // Best-effort auto-detect on paste — nudge the radio if the token format
  // clearly matches the other kind.
  const handleTokenChange = (next) => {
    setToken(next);
    setError('');
    const lower = (next || '').trim();
    if (/^sk-ant-oat/i.test(lower) || /^oat_/i.test(lower)) { if (kind !== 'oauth') setKind('oauth'); }
    else if (/^sk-ant-api/i.test(lower)) { if (kind !== 'api') setKind('api'); }
  };

  const handleSubmit = async () => {
    const trimmed = token.trim();
    if (!trimmed) { setError('Token is required.'); return; }
    setSubmitting(true);
    setError('');
    try {
      const res = await WorkspaceCredentials.add({ type: kind, token: trimmed });
      onConnected?.({ mode: kind, suffix: res?.credential?.masked || '' });
      onClose?.();
    } catch (err) {
      setError(err?.message || 'Failed to save Claude credential. Please check the token and try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <MktAlertModal
      open={isOpen}
      onClose={onClose}
      title="Connect Claude"
      ariaLabel="Connect Claude"
      secondaryLabel=""
    >
      <p style={{ color: 'rgba(255,255,255,0.7)', marginTop: 0, lineHeight: 1.5 }}>
        Zibby needs a Claude credential to run your apps&apos; agent. Tokens are
        KMS-encrypted at rest and only decrypted inside your per-instance
        Fargate task. Pick one:
      </p>
      <div role="radiogroup" aria-label="Claude credential kind" style={{ display: 'flex', gap: 12, margin: '14px 0' }}>
        <label style={radioCard(kind === 'oauth', submitting)}>
          <input type="radio" name="claude-cred-kind" value="oauth" checked={kind === 'oauth'}
            onChange={() => setKind('oauth')} disabled={submitting} style={{ marginRight: 8 }} />
          <span style={{ fontWeight: 600 }}>OAuth (recommended)</span>
          <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.6)', marginTop: 4 }}>
            Mint with <code>claude setup-token</code>. Bills against your Claude Code subscription.
          </div>
        </label>
        <label style={radioCard(kind === 'api', submitting)}>
          <input type="radio" name="claude-cred-kind" value="api" checked={kind === 'api'}
            onChange={() => setKind('api')} disabled={submitting} style={{ marginRight: 8 }} />
          <span style={{ fontWeight: 600 }}>API key</span>
          <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.6)', marginTop: 4 }}>
            <code>sk-ant-api…</code> from console.anthropic.com. Bills against your Anthropic API credits.
          </div>
        </label>
      </div>
      <label htmlFor="claude-cred-token" style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 6 }}>
        {kind === 'oauth' ? 'OAuth token' : 'API key'}
      </label>
      <input
        id="claude-cred-token"
        type="password"
        value={token}
        placeholder={kind === 'oauth' ? 'sk-ant-oat01-…' : 'sk-ant-api03-…'}
        disabled={submitting}
        autoComplete="off"
        spellCheck={false}
        onChange={(e) => handleTokenChange(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter' && !submitting && token.trim()) { e.preventDefault(); handleSubmit(); } }}
        style={inputStyle}
      />
      {error && <p role="alert" style={{ color: '#f87171', fontSize: 13, marginTop: 8 }}>{error}</p>}
      <div style={{ marginTop: 16 }}>
        <PillButton onClick={handleSubmit} disabled={submitting || !token.trim()} loading={submitting} className="btn-fullwidth">
          Save
        </PillButton>
      </div>
    </MktAlertModal>
  );
}
